import React, { useMemo, useState, useEffect } from "react";
import { format, formatDistanceStrict } from "date-fns";
import { ru } from "date-fns/locale";
import {
  FaTruck,
  FaTimesCircle,
  FaMapMarkerAlt,
  FaUser,
  FaMoneyBillWave,
} from "react-icons/fa";
import api from "../../../../services/api"; // проверь путь

export default function RouteActivityModal({ route, onClose }) {
  // храним актуальный маршрут локально
  const [currentRoute, setCurrentRoute] = useState(route);
  const [refreshing, setRefreshing] = useState(false);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

    useEffect(() => {
    if (!route?.id) return;

    const load = async () => {
      try {
        setRefreshing(true);
        const freshRoute = await api.get(`/routes/${route.id}`);
        setCurrentRoute(freshRoute);
      } catch (e) {
        console.error("Не удалось обновить маршрут", e);
      } finally {
        setRefreshing(false);
      }
    };

    load();
  }, [route?.id]);

  const receipts = currentRoute?.receipts || [];

  const checkNotNeededPaymentTypes = [
    "ПоСчету",
    "Кредит",
    "Взаимозачет",
    "Дилер",
    "БезОплаты",
  ];

  // ===== ОБНОВИТЬ ИЗ БЭКА =====
  const handleRefresh = async () => {
    if (!currentRoute?.id) return;
    try {
      setRefreshing(true);
      const freshRoute = await api.get(`/routes/${currentRoute.id}`);
      setCurrentRoute(freshRoute);
    } catch (e) {
      console.error("Не удалось обновить маршрут", e);
      alert("Не удалось обновить маршрут");
    } finally {
      setRefreshing(false);
    }
  };

  // ===== ПОДГОТОВКА ДАННЫХ ПО КВИТАНЦИЯМ =====
  const events = useMemo(
    () =>
      receipts.map((r) => {
        const products = r.products || [];
        const markedProducts = products.filter((p) => p.is_marked);

        let expectedQty = 0;
        let scannedMarks = 0;
        const marksTimes = [];

        markedProducts.forEach((p) => {
          const qty =
            Number(p.actual_quantity ?? p.planned_quantity ?? p.quantity ?? 0) ||
            0;
          expectedQty += qty;

          const marksCount = Array.isArray(p.marks) ? p.marks.length : 0;
          scannedMarks += marksCount;

          if (p.marks_changed_at) {
            marksTimes.push(new Date(p.marks_changed_at));
          }
        });

        const hasScans = scannedMarks > 0;
        let visitTime = null;

        if (hasScans) {
          if (marksTimes.length > 0) {
            visitTime = new Date(
              Math.max.apply(
                null,
                marksTimes.map((d) => d.getTime())
              )
            );
          } else if (r.status_changed_at) {
            visitTime = new Date(r.status_changed_at);
          }
        } else {
          if (r.status !== "Открыта" && r.status_changed_at) {
            visitTime = new Date(r.status_changed_at);
          } else {
            visitTime = null;
          }
        }

        const amount =
          r.actual_total ?? r.planned_total ?? r.amount ?? r.to_pay ?? 0;

        return {
          id: r.id,
          status: r.status,
          client: r.client_name,
          address: r.delivery_address,
          amount,
          departed: r.departed_at ? new Date(r.departed_at) : null,
          arrived: r.arrived_at ? new Date(r.arrived_at) : null,
          changed: r.status_changed_at ? new Date(r.status_changed_at) : null,
          reason: r.reason,
          comment: r.dispatcher_comment,
          productsCount: products.length,
          expectedQty,
          scannedMarks,
          hasScans,
          visitTime,
          paymentType: r.payment_type,
          isCheckIssued: r.is_check_issued,
          isCheckRefunded: r.is_check_refunded,
        };
      }),
    [receipts]
  );

  // порядок в таблице
  const tableEvents = useMemo(() => {
    const visited = events
      .filter((e) => e.visitTime)
      .sort((a, b) => a.visitTime - b.visitTime);

    const notVisited = events.filter((e) => !e.visitTime);

    const openNoScan = notVisited.filter(
      (e) => e.status === "Открыта" && !e.hasScans
    );
    const othersNotVisited = notVisited.filter(
      (e) => !(e.status === "Открыта" && !e.hasScans)
    );

    return [...visited, ...othersNotVisited, ...openNoScan];
  }, [events]);

  // последняя "нормальная" точка для машины
  const lastGoodId = useMemo(() => {
    if (tableEvents.length === 0) return null;

    let last = null;

    tableEvents.forEach((e) => {
      const noScansButExpected =
        e.status !== "Не доставлено" &&
        e.expectedQty > 0 &&
        e.scannedMarks === 0;

      if (!noScansButExpected) {
        last = e.id;
      }
    });

    return last;
  }, [tableEvents]);

  // ===== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ (НЕ хуки) =====
  const handleDotClick = (id) => {
    setHighlightedRowId(id);
    setTimeout(() => setHighlightedRowId(null), 600);
  };

  const formatTime = (t) =>
    t ? format(new Date(t), "HH:mm", { locale: ru }) : "—";

  const getStay = (a, c) => {
    if (!a || !c) return "—";
    return formatDistanceStrict(new Date(a), new Date(c), { locale: ru });
  };

  const getCheckStatus = (r) => {
    // 👇 если уже НЕ ДОСТАВЛЕНО — не пишем "В пути" / "Нужно пробить"
    if (r.status === "Не доставлено") {
      return { label: "Уже не нужен", color: "#777" }; // можно поменять текст, если хочешь
    }

    const isCheckNotNeeded = checkNotNeededPaymentTypes.includes(
      r.paymentType
    );

    if (isCheckNotNeeded) {
      return { label: "Чек не нужен", color: "#777" };
    }

    if (r.isCheckRefunded) {
      return { label: "Возвращён", color: "#6a1b9a" };
    }

    if (r.isCheckIssued) {
      return { label: "Пробит", color: "#2e7d32" };
    }

    // всё отсканировано, чек нужен, но не пробит
    if (r.expectedQty > 0 && r.scannedMarks === r.expectedQty) {
      return { label: "Нужно пробить", color: "#ef6c00" }; // оранжевый
    }

    // 0 сканов → "В пути"
    if (r.expectedQty > 0 && r.scannedMarks === 0) {
      return { label: "В пути", color: "#0277bd" };
    }

    // >0, но < expected → "Не все сканы"
    if (r.expectedQty > 0 && r.scannedMarks < r.expectedQty) {
      return { label: "Не все сканы", color: "#ffb300" };
    }

    // дефолт
    return { label: "В пути", color: "#0277bd" };
  };


  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1200,
      }}
    >
      <div
        style={{
          background: "#fff",
          width: "95vw",
          height: "90vh",
          borderRadius: "14px",
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <h2 style={{ color: "#1a1a2e", margin: 0 }}>
            🚚 Активность маршрута №{currentRoute?.route_number}
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={handleRefresh}
              disabled={refreshing || !currentRoute?.id}
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                border: "1px solid #4a6cf7",
                background: refreshing ? "#e0e7ff" : "#f5f7ff",
                color: "#4a6cf7",
                fontSize: 12,
                cursor: refreshing ? "default" : "pointer",
              }}
            >
              {refreshing ? "Обновление..." : "Обновить"}
            </button>
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "#777",
                fontSize: "22px",
                cursor: "pointer",
              }}
            >
              <FaTimesCircle />
            </button>
          </div>
        </div>

        {/* Водитель / команда */}
        <div style={{ marginBottom: 10, color: "#333" }}>
          <p style={{ margin: 0 }}>
            <FaUser /> Водитель: <b>{currentRoute?.driver || "—"}</b> | Грузчики:{" "}
            <b>
              {currentRoute?.loader1 || "—"}, {currentRoute?.loader2 || "—"}
            </b>
          </p>
        </div>

        {/* ЛИНИЯ */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 30,
            padding: "0 40px",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "60px",
              right: "60px",
              height: "4px",
              background: "#d9dce4",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          />

          {tableEvents.map((e) => {
            const baseColor =
              e.status === "Закрыта"
                ? "#4caf50"
                : e.status === "Не доставлено"
                ? "#f44336"
                : "#2196f3";

            const isOpenPartialScan =
              e.status === "Открыта" &&
              e.expectedQty > 0 &&
              e.scannedMarks > 0 &&
              e.scannedMarks < e.expectedQty;

            const { label: checkLabel } = getCheckStatus(e);
            const needCheckOrange = checkLabel === "Нужно пробить";

            let dotColor = baseColor;
            if (isOpenPartialScan) {
              dotColor = "#ffeb3b";
            } else if (needCheckOrange) {
              dotColor = "#ff9800";
            }

            const pointTime =
              e.visitTime || e.arrived || e.departed || e.changed;

            const isCarHere = lastGoodId != null && e.id === lastGoodId;

            return (
              <div
                key={e.id}
                style={{
                  zIndex: 2,
                  textAlign: "center",
                  flex: 1,
                  cursor: "pointer",
                  minWidth: 0,
                }}
                title={`${e.client || ""}\n${e.address || ""}`}
                onClick={() => handleDotClick(e.id)}
              >
                <div
                  style={{
                    position: "relative",
                    width: 26,
                    height: 26,
                    margin: "0 auto",
                  }}
                >
                  <div
                    style={{
                      width: 18,
                      height: 18,
                      background: dotColor,
                      borderRadius: "50%",
                      margin: "0 auto",
                      boxShadow: `0 0 8px ${dotColor}`,
                      border: "2px solid #fff",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                  {isCarHere && (
                    <FaTruck
                      size={24}
                      color="#4a6cf7"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -120%)",
                      }}
                    />
                  )}
                </div>

                <div
                  style={{
                    fontSize: 12,
                    color: "#333",
                    marginTop: 4,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 120,
                    marginInline: "auto",
                  }}
                >
                  {e.status}
                </div>
                <div style={{ fontSize: 11, color: "#777" }}>
                  {pointTime ? format(pointTime, "HH:mm", { locale: ru }) : "—"}
                </div>
              </div>
            );
          })}
        </div>

        {/* ТАБЛИЦА */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            borderTop: "1px solid #eee",
            paddingTop: 10,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: "#f9faff", color: "#333" }}>
                <th style={th}>#</th>
                <th style={th}>Клиент</th>
                <th style={th}>Адрес</th>
                <th style={th}>Сумма</th>
                <th style={th}>Статус</th>
                <th style={th}>Сканы</th>
                <th style={th}>Чек</th>
                <th style={th}>Выезд</th>
                <th style={th}>Прибыл</th>
                <th style={th}>Закрыл / сканы</th>
                <th style={th}>На месте</th>
                <th style={th}>Причина</th>
                <th style={th}>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {tableEvents.map((r, idx) => {
                const color =
                  r.status === "Закрыта"
                    ? "#4caf50"
                    : r.status === "Не доставлено"
                    ? "#f44336"
                    : "#2196f3";

                const isOpenWithScans =
                  r.status === "Открыта" && r.scannedMarks > 0;

                const noScansButExpected =
                  r.status !== "Не доставлено" &&
                  r.expectedQty > 0 &&
                  r.scannedMarks === 0;

                let rowBg = "transparent";

                if (noScansButExpected) {
                  rowBg = "rgba(244, 67, 54, 0.10)";
                }

                if (isOpenWithScans) {
                  rowBg = "rgba(255, 235, 59, 0.25)";
                }

                if (highlightedRowId === r.id) {
                  rowBg = "rgba(0, 123, 255, 0.18)";
                }

                const { label: checkLabel, color: checkColor } =
                  getCheckStatus(r);

                return (
                  <tr
                    key={r.id}
                    style={{
                      borderBottom: "1px solid #eee",
                      background: rowBg,
                      transition: "background 0.2s ease",
                    }}
                  >
                    <td style={td}>{idx + 1}</td>
                    <td style={td}>{r.client || "—"}</td>
                    <td style={td}>
                      {r.address && (
                        <>
                          <FaMapMarkerAlt /> {r.address}
                        </>
                      )}
                    </td>
                    <td style={td}>
                      <FaMoneyBillWave color="#2e7d32" />{" "}
                      {Number(r.amount || 0).toFixed(2)} ₽
                    </td>
                    <td style={{ ...td, color }}>
                      {r.status}
                    </td>

                    <td style={td}>
                      {r.expectedQty > 0
                        ? `${r.scannedMarks}/${r.expectedQty}`
                        : r.scannedMarks}
                    </td>

                    <td
                      style={{
                        ...td,
                        color: checkColor,
                        fontWeight:
                          checkLabel === "Чек не нужен" ||
                          checkLabel === "В пути"
                            ? 500
                            : 600,
                      }}
                    >
                      {checkLabel}
                    </td>

                    <td style={td}>{formatTime(r.departed)}</td>
                    <td style={td}>{formatTime(r.arrived)}</td>
                    <td style={td}>
                      {r.visitTime ? formatTime(r.visitTime) : "—"}
                    </td>
                    <td style={td}>{getStay(r.arrived, r.visitTime)}</td>
                    <td style={td}>{r.reason || "—"}</td>
                    <td style={td}>{r.comment || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const th = {
  padding: "4px 6px",
  textAlign: "left",
  fontWeight: 400,
  borderBottom: "1px solid #e5e7eb", // более светлая тонкая линия
};

const td = {
  padding: "4px 6px",     // было 6px 8px
  verticalAlign: "top",
};